/**
 * ThemeProvider for AMZEL Nest v2 design preview.
 *
 * Manages a class-based light/dark toggle by setting `.dark` on `<html>`.
 * Default: respect `prefers-color-scheme`. Persisted to localStorage.
 *
 * On unmount the previously set classes are restored so the existing
 * `dossier-frontend` IT-Dark-Layout (Landing.tsx / SiteDetail.tsx) keeps
 * its hard-coded look untouched.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Moon, Sun } from "lucide-react";

type Mode = "light" | "dark";
const STORAGE_KEY = "amzel-nest-v2-theme";

interface ThemeContextValue {
  mode: Mode;
  toggle: () => void;
  setMode: (mode: Mode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function detectInitial(): Mode {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* localStorage may be blocked */
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(detectInitial);

  // Apply `.dark` class to <html>, restore previous classes on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains("dark");
    if (mode === "dark") html.classList.add("dark");
    else html.classList.remove("dark");
    return () => {
      // Restore baseline (existing app expects .dark — see index.html).
      if (wasDark) html.classList.add("dark");
      else html.classList.remove("dark");
    };
  }, [mode]);

  const setMode = useCallback((next: Mode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const toggle = useCallback(() => {
    setModeState((m) => {
      const next = m === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

export function ThemeToggle() {
  const { mode, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={mode === "dark" ? "Auf hellen Modus wechseln" : "Auf dunklen Modus wechseln"}
      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
    >
      {mode === "dark" ? (
        <>
          <Sun className="size-4" aria-hidden />
          <span className="hidden sm:inline">Hell</span>
        </>
      ) : (
        <>
          <Moon className="size-4" aria-hidden />
          <span className="hidden sm:inline">Dunkel</span>
        </>
      )}
    </button>
  );
}
